import { type AbstractUser } from "@/user";

import { Pages } from "../../constants";

import * as types from "./types";

/**
 * @deprecated
 * Please refer to {@link Logger}.  Amplitude-related behavior is now consolidated into the central
 * logger class, {@link Logger}.  Event logging can be done by calling {@link Logger.event}.
 */
export const configureAmplitudeInstance = <
  U extends AbstractUser,
  A extends types.Amplitude_Deprecated<O, I, IFn>,
  O,
  I extends types.AmplitudeIdentify_Deprecated,
  IFn extends types.Amplitude_identify<I>,
>(params: {
  amplitude: A;
  apiKey: string;
  options?: O;
  setters?: (cls: I) => void;
  getInstance: () => Pick<A, "init" | "logEvent">;
}): types.ConfiguredAmplitude<U, A, O, I, IFn> => {
  const logEvent: A["logEvent"] = ((...args: Parameters<A["logEvent"]>) => {
    const inst = params.getInstance();
    return inst.logEvent(...args);
  }) as A["logEvent"];

  const base = {
    initialize(user?: U) {
      const instance = params.getInstance();
      instance.init(
        params.apiKey,
        user ? user.authedUserId ?? user.displayUserId : undefined,
        params.options,
      );
    },
    identifyWeb(user: U) {
      const identify = new params.amplitude.Identify()
        .set("Email", user.email)
        .set("Display user ID", user.displayUserId)
        .set("Authed user ID", user.authedUserId ?? user.displayUserId)
        .set("isAdmin", user.isAdmin);

      params.setters && params.setters(identify as I);
      params.amplitude.identify(identify as I);
    },
    identify(user: U) {
      this.initialize(user);

      const identify = new params.amplitude.Identify()
        .set("Email", user.email)
        .set("Display user ID", user.displayUserId)
        .set("Authed user ID", user.authedUserId ?? user.displayUserId)
        .set("isAdmin", user.isAdmin)
        .set("isMobile", true);

      params.setters && params.setters(identify as I);
      params.amplitude.identify(identify as I);
    },
    logEvent,
  };

  const _prettyLogEvent = <T>({
    page,
    domain,
    action,
    data,
    additionalActionInfo,
    user,
  }: types.LogEventParams<U, T>) => {
    const pg = Pages.getModel(page);
    return base.logEvent(
      `${typeof pg.label === "function" ? pg.label(user) : pg.label} Page ->${
        domain ? ` ${domain} ->` : ""
      } ${action}${additionalActionInfo ? `:${additionalActionInfo}` : ""}`,
      {
        ...data,
        isDashboardV2Enabled: user.featureFlags.enableDashboardV2 ?? false,
      },
    );
  };

  return {
    ...base,
    prettyLogEvent: Object.assign(
      _prettyLogEvent,
      types.LogEventActions.values.reduce(
        (
          prev: types.PrettyLogEventActionMethods<U>,
          action: string,
        ): types.PrettyLogEventActionMethods<U> => ({
          ...prev,
          [types.LogMethodNames[action as types.LogEventAction]]: (
            p: Omit<types.LogEventParams<U, unknown>, "action">,
          ) =>
            _prettyLogEvent({
              ...p,
              action: action as types.LogEventAction,
            }),
        }),
        {} as types.PrettyLogEventActionMethods<U>,
      ),
    ),
  };
};
